/**
 * Session route — handles /session/new and /session/:sessionId.
 *
 * New session (/session/new or /session/new?mode=voice|text):
 *   - If no mode in query, renders ModeToggle for selection
 *   - ModeToggle shows SuggestionBanner above mode buttons (Phase 9 D-12)
 *   - On mode select: generates a new ULID sessionId, navigates replace to /session/{id}?mode=...
 *   - If suggestion was accepted, focusAreaId is appended to the URL
 *   - Renders InterviewSession with no preloaded turns
 *
 * Existing session (/session/:sessionId):
 *   - Fetches GET /sessions/:id/turns + GET /sessions to find session metadata
 *   - If status='ended': renders InterviewSession with readOnly=true
 *   - If status='active': renders InterviewSession with preloadedTurns (resume mode)
 *   - If lastActiveAt > 24h ago: renders read-only (stale session)
 */

import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { InterviewSession } from '../components/InterviewSession/InterviewSession';
import { ModeToggle } from '../components/InterviewSession/ModeToggle';
import { fetchCurriculum, patchSessionTags, type CurriculumArea } from '../lib/curriculum-api';
import { patchStoryTopic } from '../lib/story-topics-api';
import { reopenSession } from '../lib/session-api';
import { newSessionId, isValidUlid } from '../lib/idGenerator';
import type { Turn } from '../store/sessionStore';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SessionMeta {
  sessionId: string;
  mode: 'voice' | 'text';
  status: 'active' | 'ended';
  startedAt: string;
  lastActiveAt?: string;
  /** Auto-generated title (Phase 5) */
  title?: string;
  /** Area IDs from classifier (Phase 8) */
  areaTags?: string[];
  /** ISO timestamp of last classification */
  areaTagsAt?: string;
}

interface TurnRaw {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

// ── Helper ────────────────────────────────────────────────────────────────────

async function getIdToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No id_token');
  return token;
}

// ── Session route ─────────────────────────────────────────────────────────────

export function Session() {
  const { sessionId: routeSessionId } = useParams<{ sessionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const modeParam = searchParams.get('mode') as 'voice' | 'text' | null;
  // photoId from URL query param — present for photo-prompted sessions (02-05)
  const photoIdParam = searchParams.get('photoId') ?? undefined;
  // focusAreaId from URL query param — present when suggestion was accepted (Phase 9 D-15)
  const focusAreaIdParam = searchParams.get('focusAreaId') ?? undefined;
  // topicTitle from URL query param — present when story topic was chosen (Phase 30)
  // searchParams.get auto-decodes percent-encoding — do NOT manually decode
  const topicTitleParam = searchParams.get('topicTitle') ?? undefined;

  // ── /session/new branch ────────────────────────────────────────────────────
  // Route is /session/new OR /session/:sessionId where sessionId === 'new'
  const isNew = routeSessionId === 'new' || !routeSessionId;

  // For new sessions: track whether mode has been selected
  const [newMode, setNewMode] = useState<'voice' | 'text' | null>(modeParam ?? null);
  const [generatedId, setGeneratedId] = useState<string | null>(null);

  // focusAreaId, focusTopicId, focusTopicTitle set from SuggestionBanner via ModeToggle callback
  const [focusAreaId, setFocusAreaId] = useState<string | null>(null);
  const [focusTopicId, setFocusTopicId] = useState<string | null>(null);
  const [focusTopicTitle, setFocusTopicTitle] = useState<string | null>(null);
  // photoId uploaded in the pre-session photo picker (ModeToggle)
  const [preSessionPhotoId, setPreSessionPhotoId] = useState<string | null>(null);

  const handleModeSelect = (selectedMode: 'voice' | 'text') => {
    // Prime AudioContext during the gesture so the AI opening greeting plays on iOS Safari
    // and Android Chrome without requiring the user to tap the mic first.
    // iOS 14.5+ / Chrome 71+: once any AudioContext is resumed in a gesture, subsequent
    // contexts start in `running` state.
    if (selectedMode === 'voice') {
      try {
        const primer = new AudioContext();
        void primer.resume().then(() => primer.close());
      } catch { /* AudioContext unavailable — audio plays via fallback path */ }
    }

    const id = newSessionId();
    setGeneratedId(id);
    setNewMode(selectedMode);
    const effectivePhotoId = preSessionPhotoId ?? photoIdParam;
    const photoQuery = effectivePhotoId ? `&photoId=${effectivePhotoId}` : '';
    const focusQuery = focusAreaId ? `&focusAreaId=${focusAreaId}` : '';
    const topicQuery = focusTopicTitle ? `&topicTitle=${encodeURIComponent(focusTopicTitle)}` : '';
    setSearchParams({ mode: selectedMode }, { replace: true });
    navigate(`/session/${id}?mode=${selectedMode}${photoQuery}${focusQuery}${topicQuery}`, { replace: true });
    // Auto-tag the story topic with this session so the book builder can link them
    if (focusTopicId) {
      void patchStoryTopic(focusTopicId, { status: 'captured', sessionIds: [id] });
    }
  };

  // ── /session/:sessionId branch ─────────────────────────────────────────────
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sessionMeta, setSessionMeta] = useState<SessionMeta | null>(null);
  const [preloadedTurns, setPreloadedTurns] = useState<Turn[]>([]);
  const [resolvedMode, setResolvedMode] = useState<'voice' | 'text'>(modeParam ?? 'text');

  // ── Continue session state ───────────────────────────────────────────────
  const [continued, setContinued] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);
  const [continueError, setContinueError] = useState<string | null>(null);

  async function handleContinue() {
    if (!routeSessionId) return;
    setContinueLoading(true);
    setContinueError(null);
    try {
      await reopenSession(routeSessionId);
      setContinued(true);
    } catch {
      setContinueError('Failed to reopen session. Please try again.');
    } finally {
      setContinueLoading(false);
    }
  }

  // ── Tag chip state (read-only sessions only) ─────────────────────────────
  const [areaTags, setAreaTags] = useState<string[]>([]);
  const [allAreas, setAllAreas] = useState<CurriculumArea[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    if (!routeSessionId || !isValidUlid(routeSessionId)) {
      setLoadError('Invalid session ID');
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        const token = await getIdToken();
        const restApiUrl = import.meta.env.VITE_REST_API_URL ?? '';

        // Fetch session list to find metadata for this session
        const [turnsRes, sessionsRes] = await Promise.all([
          fetch(`${restApiUrl}/sessions/${routeSessionId}/turns`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${restApiUrl}/sessions`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!turnsRes.ok || !sessionsRes.ok) {
          throw new Error(
            `Failed to load session (turns: ${turnsRes.status}, sessions: ${sessionsRes.status})`
          );
        }

        const [turnsBody, sessionsBody] = await Promise.all([
          turnsRes.json() as Promise<{ turns: TurnRaw[] }>,
          sessionsRes.json() as Promise<{ sessions: SessionMeta[] }>,
        ]);

        if (cancelled) return;

        // Find this session in the list
        const meta = sessionsBody.sessions.find((s) => s.sessionId === routeSessionId) ?? null;
        setSessionMeta(meta);

        if (meta) {
          setResolvedMode(meta.mode);
        } else if (modeParam) {
          setResolvedMode(modeParam);
        }

        // Convert raw turns to Turn shape
        const turns: Turn[] = (turnsBody.turns ?? []).map((t: TurnRaw) => ({
          id: t.id,
          role: t.role,
          content: t.content,
          createdAt: t.createdAt,
        }));
        setPreloadedTurns(turns);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load session');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [routeSessionId, modeParam]);

  // ── Tag chip data load (read-only sessions only) ──────────────────────────
  useEffect(() => {
    // Only load tag data for existing ended sessions
    if (isNew || !routeSessionId || loading) return;
    let cancelled = false;

    async function loadTagData() {
      setTagsLoading(true);
      try {
        const curriculumData = await fetchCurriculum();
        if (!cancelled) {
          setAllAreas(curriculumData.areas);
          // Pick up areaTags from sessionMeta (set after load completes)
          const tags = sessionMeta?.areaTags ?? [];
          setAreaTags(tags);
        }
      } catch {
        // Non-blocking — tag chips hidden on error
      } finally {
        if (!cancelled) setTagsLoading(false);
      }
    }

    void loadTagData();
    return () => { cancelled = true; };
  }, [isNew, routeSessionId, loading, sessionMeta]);

  // ── Tag toggle handler ────────────────────────────────────────────────────
  async function toggleTag(areaId: string) {
    if (!routeSessionId) return;
    const currentTags = areaTags;
    const newTags = currentTags.includes(areaId)
      ? currentTags.filter((t) => t !== areaId)
      : [...currentTags, areaId];
    // Optimistic update
    setAreaTags(newTags);
    try {
      await patchSessionTags(routeSessionId, newTags);
    } catch {
      // Revert on failure
      setAreaTags(currentTags);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  // New session: show ModeToggle (SuggestionBanner is embedded inside ModeToggle, Phase 9 D-12)
  if (isNew && !newMode && !generatedId) {
    return (
      <div className="min-h-dvh bg-stone-50 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <ModeToggle
            onSelect={handleModeSelect}
            onFocusAreaId={(id, topicId, topicTitle) => { setFocusAreaId(id); if (topicId) setFocusTopicId(topicId); if (topicTitle) setFocusTopicTitle(topicTitle); }}
            onPhotoId={(photoId) => setPreSessionPhotoId(photoId)}
          />
        </div>
      </div>
    );
  }

  // Loading existing session
  if (loading) {
    return (
      <div className="min-h-dvh bg-stone-50 flex items-center justify-center">
        <div className="text-stone-500 text-sm">Loading session…</div>
      </div>
    );
  }

  // Load error
  if (loadError) {
    return (
      <div className="min-h-dvh bg-stone-50 flex items-center justify-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-sm text-center">
          <p className="text-red-700 text-sm font-medium">{loadError}</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 rounded-lg bg-stone-800 text-white text-sm hover:bg-stone-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Determine if this session is read-only
  // Ended sessions or active sessions where lastActiveAt > 24h are read-only
  const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
  const isStale = sessionMeta?.lastActiveAt
    ? Date.now() - new Date(sessionMeta.lastActiveAt).getTime() > TWENTY_FOUR_HOURS_MS
    : false;
  const isReadOnly = (sessionMeta?.status === 'ended' || isStale) && !continued;

  // For new sessions redirecting to their final ID
  const finalSessionId = routeSessionId ?? generatedId ?? newSessionId();
  const finalMode = isNew ? (newMode ?? 'text') : resolvedMode;

  // focusAreaId: from state (set via ModeToggle callback) or from URL param
  const effectiveFocusAreaId = focusAreaId ?? focusAreaIdParam;

  return (
    <div className="flex flex-col h-dvh">
      <InterviewSession
        sessionId={finalSessionId}
        mode={finalMode}
        preloadedTurns={preloadedTurns.length > 0 ? preloadedTurns : undefined}
        readOnly={isReadOnly}
        photoId={isReadOnly ? undefined : photoIdParam}
        focusAreaId={isNew ? effectiveFocusAreaId : undefined}
        topicTitle={isNew ? (focusTopicTitle ?? topicTitleParam) : undefined}
        areaTags={isReadOnly ? sessionMeta?.areaTags : undefined}
        areaTagsAt={isReadOnly ? sessionMeta?.areaTagsAt : undefined}
      />
      {/* Continue session bar — shown on read-only sessions before continuing */}
      {isReadOnly && !continued && (
        <div className="flex-shrink-0 bg-white border-t border-stone-200 px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-sm text-stone-500">This session has ended.</p>
          <div className="flex items-center gap-2">
            {continueError && (
              <span className="text-xs text-red-600">{continueError}</span>
            )}
            <button
              type="button"
              onClick={() => void handleContinue()}
              disabled={continueLoading}
              className="px-4 py-1.5 rounded-lg bg-stone-800 text-white text-sm font-medium hover:bg-stone-700 disabled:opacity-50 transition-colors"
            >
              {continueLoading ? 'Reopening…' : 'Continue Session'}
            </button>
          </div>
        </div>
      )}
      {/* Tag review chips — shown on ended sessions (D-17, TAG-02) */}
      {isReadOnly && !tagsLoading && allAreas.length > 0 && (
        <div className="flex-shrink-0 bg-white border-t border-stone-200 px-4 py-3">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
            Life areas covered in this session
          </p>
          <div className="flex flex-wrap gap-2">
            {allAreas.map((area) => {
              const isActive = areaTags.includes(area.id);
              return (
                <button
                  key={area.id}
                  type="button"
                  onClick={() => void toggleTag(area.id)}
                  aria-label={`${area.title} — click to toggle`}
                  aria-pressed={isActive}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                    isActive
                      ? 'bg-amber-100 text-amber-700 border-amber-300'
                      : 'bg-stone-100 text-stone-500 border-stone-200 hover:bg-stone-200'
                  }`}
                >
                  {area.title}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
